/* eslint-disable @typescript-eslint/indent */
/* eslint-disable multiline-ternary */
import { FormattedMessage, useIntl } from 'react-intl'
import BasePageLayout from '../../../common/components/BasePageLayout'
import ReportSummary from '../../../../../components/Reports/ReportSummary'
import messages from './messages'
import PageHeader from '../../../../../components/InternalAppShell/PageHeader'
import {
  Flex,
  Img,
  Button,
  Grid,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react'
import { HomeIcon, PlusIcon } from '@heroicons/react/24/outline'
import useLatestPatients from '../../hooks/useLatestPatients'
import LatestPatients from '../../components/LatestPatients'
import TodayQueue from '../../components/TodayQueue'
import Banner from '../../../common/components/Banner'
import { useAccount } from '../../../account/AccountProvider'
import {
  differenceBetweenTodayAndDate,
  isLessThanFourteenDaysLeft
} from '../../../../../utils/dateUtils'
import PlanExpiringBanner from '../../components/PlanExpiringBanner'
import TrialEndingBanner from '../../components/TrialEndingBanner'
import ReferralBanner from '../../components/ReferralBanner'
import { useContactTrigger } from '../../../common/hooks/useContactTrigger'
import PatientModal from '../../../patient/components/PatientModal'
import MiniCalendar from '../../components/MiniCalendar'
import { useRole } from '../../../../../providers/RoleProvider'
import { normalizeAppointmentSettings } from '../../../../../utils/appointmentUtils'
import { useAuthState } from '../../../../../auth/AuthProvider'

function Dashboard() {
  const { formatMessage } = useIntl()
  const { doctorId } = useRole()
  const { uid } = useAuthState()

  const {
    isFreeMaxUsageReached,
    hasTimedAccess,
    timedAccessUntil,
    freeMonthlyLimit,
    monthlyUsage,
    schema,
    appointmentSettings,
    loading: doctorProfileLoading
  } = useAccount()

  const { latestPatients, loading: latestPatientsLoading } = useLatestPatients()

  const remainingVisits = Math.max(0, freeMonthlyLimit - monthlyUsage)
  const remainingDays = differenceBetweenTodayAndDate(timedAccessUntil)
  const normalizedAppointmentSettings =
    normalizeAppointmentSettings(appointmentSettings)

  const { triggerContact } = useContactTrigger()
  const {
    isOpen: isPatientDrawerOpen,
    onOpen: onPatientDrawerOpen,
    onClose: onPatientDrawerClose
  } = useDisclosure()

  return (
    <BasePageLayout
      isCentered
      contentMode="wide"
      title={formatMessage(messages.pageTitle)}
    >
      <PageHeader
        title={formatMessage(messages.pageTitle)}
        description={formatMessage({ id: 'page.dashboardDescription' })}
        icon={HomeIcon}
        actions={
          <Tooltip
            label={
              isFreeMaxUsageReached
                ? formatMessage(messages.freeUsageLimitReached)
                : undefined
            }
            hasArrow
            shouldWrapChildren
          >
            <Button
              size="sm"
              leftIcon={<PlusIcon width={16} />}
              aria-label="Add patient"
              isDisabled={isFreeMaxUsageReached}
              onClick={onPatientDrawerOpen}
            >
              {formatMessage({ id: 'page.addPatient' })}
            </Button>
          </Tooltip>
        }
      />

      {!hasTimedAccess && remainingVisits < 20 && !doctorProfileLoading ? (
        <TrialEndingBanner remainingVisits={remainingVisits} />
      ) : null}
      {hasTimedAccess &&
      isLessThanFourteenDaysLeft(timedAccessUntil) &&
      !doctorProfileLoading ? (
        <PlanExpiringBanner remainingDays={remainingDays} />
      ) : null}

      <Grid
        templateColumns={{
          base: 'minmax(0, 1fr)',
          lg: 'repeat(2, minmax(0, 1fr))'
        }}
        gap={4}
        alignItems="stretch"
      >
        <ReportSummary family="analytics" />
        {doctorId && uid && (
          <MiniCalendar
            ownerId={doctorId}
            actorId={uid}
            settings={normalizedAppointmentSettings}
          />
        )}
      </Grid>
      <Flex flexDir="column" gap={4}>
        <TodayQueue />
        <Grid
          templateColumns={{
            base: 'minmax(0, 1fr)',
            md: 'repeat(2, minmax(0, 1fr))'
          }}
          gridGap={4}
        >
          <ReportSummary family="finance" />
          <LatestPatients
            latestPatients={latestPatients}
            latestPatientsLoading={latestPatientsLoading}
          />
        </Grid>
      </Flex>

      <ReferralBanner />

      <Banner
        media={<Img src="/sijilo.png" alt="logo" width="10%" />}
        title={formatMessage(messages.welcome)}
        description={formatMessage(messages.hereForYou)}
        action={
          <Button colorScheme="blue" size="md" onClick={triggerContact}>
            <FormattedMessage {...messages.contactUs} />
          </Button>
        }
      />

      <PatientModal
        historySchema={schema.history}
        isOpen={isPatientDrawerOpen}
        onClose={onPatientDrawerClose}
      />
    </BasePageLayout>
  )
}

export default Dashboard
